import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BiCheckCircle,
  BiXCircle,
  BiLink,
  BiSignal4,
  BiSolidPen,
  BiLoaderAlt,
  BiSearch,
  BiReset,
} from "react-icons/bi";
import { protocolsService, Protocol } from "../../services/protocolsService";
import toast, { Toaster } from "react-hot-toast";
import ProtocolFilters from "../../components/Protocols/ProtocolFilters";
import Pagination from "../../components/Pagination";

const ProtocolsTable = () => {
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(
    sessionStorage.getItem("protocolFilter") || ""
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [protocolsPerPage] = useState(50);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedProtocols, setSelectedProtocols] = useState<number[]>([]);
  const [showCurrentProtocols, setShowCurrentProtocols] = useState<
    "current" | "archive" | "department" | "unassigned"
  >(
    (sessionStorage.getItem("protocolsView") as
      | "current"
      | "archive"
      | "department"
      | "unassigned") || "current"
  );
  const [checkingSignatures, setCheckingSignatures] = useState<boolean>(false);
  const [subjectFilter, setSubjectFilter] = useState(
    sessionStorage.getItem("subjectFilter") || ""
  );
  const [specialityFilter, setSpecialityFilter] = useState(
    sessionStorage.getItem("specialityFilter") || ""
  );
  const [sessionTypeFilter, setSessionTypeFilter] = useState(
    sessionStorage.getItem("sessionTypeFilter") || ""
  );
  const [facultyNumber, setFacultyNumber] = useState(
    sessionStorage.getItem("facultyNumber") || ""
  );
  const [studentName, setStudentName] = useState(
    sessionStorage.getItem("studentName") || ""
  );
  const [courseFilter, setCourseFilter] = useState(
    sessionStorage.getItem("courseFilter") || ""
  );

  const isDepartmentHead = localStorage.getItem("isDepartmentHead") === "true";

  const { t } = useTranslation();

  const [sortConfig, setSortConfig] = useState<{
    key:
      | "protocolNumber"
      | "issueDate"
      | "examDate"
      | "academicYearName"
      | "semester"
      | "isSigned"
      | null;
    direction: "asc" | "desc";
  }>(() => {
    const savedSort = localStorage.getItem("protocols-table-sort-preference");
    return savedSort ? JSON.parse(savedSort) : { key: null, direction: "asc" };
  });

  useEffect(() => {
    if (sortConfig.key) {
      localStorage.setItem(
        "protocols-table-sort-preference",
        JSON.stringify(sortConfig)
      );
    }
  }, [sortConfig]);

  const handleSort = (
    key:
      | "protocolNumber"
      | "issueDate"
      | "examDate"
      | "academicYearName"
      | "semester"
      | "isSigned"
  ) => {
    setSortConfig((current) => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIndicator: React.FC<{ column: string }> = ({ column }) => {
    if (sortConfig.key !== column) return <span className="ml-1">↕</span>;
    return (
      <span className="ml-1">{sortConfig.direction === "asc" ? "↑" : "↓"}</span>
    );
  };

  const fetchData = useCallback(
    async (qp: any) => {
      try {
        setLoading(true);
        const data = await protocolsService.getProtocols({
          ...qp,
          archive: showCurrentProtocols === "archive",
          myDepartment: showCurrentProtocols === "department",
          showUnassigned: showCurrentProtocols === "unassigned",
        });
        setProtocols(data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    },
    [showCurrentProtocols]
  );

  useEffect(() => {
    fetchData({
      fn: facultyNumber,
      studentName: studentName,
    });
  }, [showCurrentProtocols]);

  const getUniqueValues = (field: keyof Protocol) => {
    return Array.from(new Set(protocols.map((protocol) => protocol[field])))
      .filter((value) => value)
      .sort();
  };

  const filteredProtocols = useMemo(() => {
    let filtered = protocols;

    if (subjectFilter) {
      filtered = filtered.filter(
        (protocol) => protocol.subjectName === subjectFilter
      );
    }
    if (specialityFilter) {
      filtered = filtered.filter(
        (protocol) => protocol.specialityName === specialityFilter
      );
    }
    if (sessionTypeFilter) {
      filtered = filtered.filter(
        (protocol) => protocol.sessionTypeName === sessionTypeFilter
      );
    }
    if (courseFilter) {
      filtered = filtered.filter((protocol) => {
        if (typeof protocol.semester !== "number") return false;
        const course =
          Math.floor(protocol.semester / 2) + (protocol.semester % 2);
        return course.toString() === courseFilter;
      });
    }

    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        if (sortConfig.key === "issueDate") {
          const aDate = new Date(
            a[sortConfig.key as keyof Protocol] as string
          ).getTime();
          const bDate = new Date(
            b[sortConfig.key as keyof Protocol] as string
          ).getTime();
          return sortConfig.direction === "asc" ? aDate - bDate : bDate - aDate;
        }

        if (sortConfig.key === "semester") {
          const aNum = Number(a[sortConfig.key as keyof Protocol]) || 0;
          const bNum = Number(b[sortConfig.key as keyof Protocol]) || 0;
          return sortConfig.direction === "asc" ? aNum - bNum : bNum - aNum;
        }

        if (sortConfig.key === "isSigned") {
          const aVal = a[sortConfig.key as keyof Protocol] ? 1 : 0;
          const bVal = b[sortConfig.key as keyof Protocol] ? 1 : 0;
          return sortConfig.direction === "asc" ? aVal - bVal : bVal - aVal;
        }

        const aVal = (
          a[sortConfig.key as keyof Protocol]?.toString() || ""
        ).toLowerCase();
        const bVal = (
          b[sortConfig.key as keyof Protocol]?.toString() || ""
        ).toLowerCase();
        return sortConfig.direction === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      });
    }

    if (filter) {
      filtered = filtered.filter((protocol) =>
        Object.values(protocol)
          .map((value) => value?.toString().toLowerCase() ?? "")
          .some((value) => value.includes(filter.toLowerCase()))
      );
    }

    return filtered;
  }, [protocols, subjectFilter, specialityFilter, sessionTypeFilter, courseFilter, sortConfig, filter]);

  const indexOfLastProtocol = currentPage * protocolsPerPage;
  const indexOfFirstProtocol = indexOfLastProtocol - protocolsPerPage;
  const currentProtocols = useMemo(() => filteredProtocols.slice(indexOfFirstProtocol, indexOfLastProtocol), [filteredProtocols, indexOfFirstProtocol, indexOfLastProtocol]);
  const totalPages = Math.ceil(filteredProtocols.length / protocolsPerPage);

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
    if (!selectAll) {
      const currentPageIds = currentProtocols.map(
        (protocol) => protocol.protocolId
      );
      setSelectedProtocols([...selectedProtocols, ...currentPageIds]);
    } else {
      const remainingSelected = selectedProtocols.filter(
        (id) =>
          !currentProtocols.map((protocol) => protocol.protocolId).includes(id)
      );
      setSelectedProtocols(remainingSelected);
    }
  };

  const handleSelectProtocol = (protocolId: number) => {
    if (selectedProtocols.includes(protocolId)) {
      setSelectedProtocols(selectedProtocols.filter((id) => id !== protocolId));
    } else {
      setSelectedProtocols([...selectedProtocols, protocolId]);
    }
  };

  const handleApiError = (error: unknown, defaultMessage: string) => {
    console.error(error);
    const message = error instanceof Error ? error.message : defaultMessage;
    toast.error(t(message));
  };

  // TEMPORARY: Original function commented out - uncomment when service is available
  const handleSignSelectedProtocols = async () => {
    try {
      await protocolsService.signProtocols(selectedProtocols);
      toast.success(t("Selected protocols have been processed successfully."));
      await fetchData({
        fn: facultyNumber,
        studentName: studentName,
        archive: showCurrentProtocols,
      });
    } catch (error) {
      handleApiError(error, "Failed to process selected protocols.");
    }
  };

  // TEMPORARY: Show unavailable message - remove when service is restored
  // const handleSignSelectedProtocols = () => {
  //   toast.error(t("Временно подписването с Евротръст не е налично."));
  // };

  const handleCheckProtocolsSignature = async () => {
    setCheckingSignatures(true);
    try {
      const result = await protocolsService.checkProtocolsSignature();
      if (result) {
        toast.success(
          t("Protocols signatures have been checked successfully.")
        );
        await fetchData({
          fn: facultyNumber,
          studentName: studentName,
          archive: showCurrentProtocols,
        });
      } else {
        toast.error(t("Failed to check protocols signatures."));
      }
    } catch (error) {
      handleApiError(error, "An unexpected error occurred while checking protocols signatures.");
    } finally {
      setCheckingSignatures(false);
    }
  };

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleSearch = () => {
    fetchData({
      fn: facultyNumber,
      studentName: studentName,
      archive: showCurrentProtocols,
    });
  };

  const handleReset = () => {
    setFacultyNumber("");
    setStudentName("");
    setFilter("");
    setSubjectFilter("");
    setSpecialityFilter("");
    setSessionTypeFilter("");
    setCourseFilter("");
    fetchData({
      fn: "",
      studentName: "",
      archive: showCurrentProtocols,
    });
  };

  useEffect(() => {
    sessionStorage.setItem("protocolFilter", filter);
    sessionStorage.setItem("subjectFilter", subjectFilter);
    sessionStorage.setItem("specialityFilter", specialityFilter);
    sessionStorage.setItem("sessionTypeFilter", sessionTypeFilter);
    sessionStorage.setItem("facultyNumber", facultyNumber);
    sessionStorage.setItem("studentName", studentName);
    sessionStorage.setItem("protocolsView", showCurrentProtocols);
    sessionStorage.setItem("courseFilter", courseFilter);
  }, [
    filter,
    subjectFilter,
    specialityFilter,
    sessionTypeFilter,
    facultyNumber,
    studentName,
    showCurrentProtocols,
    courseFilter,
  ]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <BiLoaderAlt className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2">{t("Loading protocols...")}</span>
      </div>
    );
  }

  return (
    <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="flex-auto p-6 shadow-sm">
        <div className="flex border-b border-gray-200 mb-4">
          <button
            onClick={() => { setShowCurrentProtocols("current"); setCurrentPage(1); }}
            className={`py-2 px-6 text-lg -mb-px ${
              showCurrentProtocols === "current"
                ? "text-blue-600 dark:text-blue-200 border-b-2 border-blue-600 bg-transparent font-bold"
                : "text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-400 border-b-2 border-transparent"
            }`}
          >
            {t("Show current protocols")}
          </button>
          <button
            onClick={() => { setShowCurrentProtocols("archive"); setCurrentPage(1); }}
            className={`py-2 px-6 text-lg -mb-px ${
              showCurrentProtocols === "archive"
                ? "text-blue-600 dark:text-blue-200 border-b-2 border-blue-600 bg-transparent font-bold"
                : "text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-400 border-b-2 border-transparent"
            }`}
          >
            {t("Show archived protocols")}
          </button>
          {isDepartmentHead && (
            <button
              onClick={() => { setShowCurrentProtocols("department"); setCurrentPage(1); }}
              className={`py-2 px-6 text-lg -mb-px ${
                showCurrentProtocols === "department"
                  ? "text-blue-600 dark:text-blue-200 border-b-2 border-blue-600 bg-transparent font-bold"
                  : "text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-400 border-b-2 border-transparent"
              }`}
            >
              {t("Show my department protocols")}
            </button>
          )}
          <button
            onClick={() => { setShowCurrentProtocols("unassigned"); setCurrentPage(1); }}
            className={`py-2 px-6 text-lg -mb-px ${
              showCurrentProtocols === "unassigned"
                ? "text-blue-600 dark:text-blue-200 border-b-2 border-blue-600 bg-transparent font-bold"
                : "text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-400 border-b-2 border-transparent"
            }`}
          >
            {t("Unassigned protocols")}
          </button>
        </div>

        <ProtocolFilters 
          facultyNumber={facultyNumber}
          setFacultyNumber={setFacultyNumber}
          studentName={studentName}
          setStudentName={setStudentName}
          filter={filter}
          setFilter={setFilter}
          subjectFilter={subjectFilter}
          setSubjectFilter={setSubjectFilter}
          specialityFilter={specialityFilter}
          setSpecialityFilter={setSpecialityFilter}
          sessionTypeFilter={sessionTypeFilter}
          setSessionTypeFilter={setSessionTypeFilter}
          courseFilter={courseFilter}
          setCourseFilter={setCourseFilter}
          onSearch={handleSearch}
          onReset={handleReset}
          getUniqueValues={(field: string) => getUniqueValues(field as keyof Protocol).map(value => String(value))}
          setCurrentPage={setCurrentPage}
        />

        <div className="block w-full overflow-auto scrolling-touch text-left">
          <table className="w-full max-w-full mb-4">
            <thead className="bg-gray-300 dark:bg-gray-700">
              <tr>
                <th className="px-4 py-2 border dark:border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th
                  className="px-4 py-2 border dark:border-gray-200 cursor-pointer hover:bg-opacity-80"
                >
                  <button
                    type="button"
                    onClick={() => handleSort("protocolNumber")}
                    className="w-full text-left focus:outline-none"
                    aria-label={t("Sort by Protocol Number")}
                  >
                    {t("ProtocolNumber")}
                    <SortIndicator column="protocolNumber" />
                  </button>
                </th>
                {showCurrentProtocols === "department" && (
                  <th className="px-4 py-2 border dark:border-gray-200">
                    {t("Lecturer")}
                  </th>
                )}
                <th className="px-4 py-2 border dark:border-gray-200 cursor-pointer hover:bg-opacity-80">
                  {t("Subject")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 cursor-pointer hover:bg-opacity-80">
                  {t("Speciality")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200">
                  {t("Exam Type")}
                </th>
                <th className="px-4 py-2 border dark:border-gray-200 cursor-pointer hover:bg-opacity-80">
                  {t("Session Type")}
                </th>
                <th
                  className="px-4 py-2 border dark:border-gray-200"
                >
                  <button
                    type="button"
                    onClick={() => handleSort("examDate")}
                    className="w-full text-left focus:outline-none"
                    aria-label={t("Sort by Exam Date")}
                  >
                    {t("Exam Date")}
                    <SortIndicator column="examDate" />
                  </button>
                </th>
                <th
                  className="px-4 py-2 border dark:border-gray-200 cursor-pointer hover:bg-opacity-80"
                  onClick={() => handleSort("academicYearName")}
                >
                  {t("Academic Year")}
                  <SortIndicator column="academicYearName" />
                </th>
                <th
                  className="px-4 py-2 border dark:border-gray-200 cursor-pointer hover:bg-opacity-80"
                  onClick={() => handleSort("semester")}
                >
                  {t("Semester")}
                  <SortIndicator column="semester" />
                </th>
                <th
                  className="px-4 py-2 border dark:border-gray-200 cursor-pointer hover:bg-opacity-80"
                  onClick={() => handleSort("isSigned")}
                >
                  {t("Signed")}
                  <SortIndicator column="isSigned" />
                </th>
                <th className="px-4 py-2 border dark:border-gray-200">
                  {t("Actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentProtocols.map((protocol) => (
                <tr
                  key={protocol.protocolId}
                  className="hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <td className="px-4 py-2 border text-center dark:border-gray-600">
                    {!protocol.isSigned && (
                      <input
                        type="checkbox"
                        checked={selectedProtocols.includes(
                          protocol.protocolId
                        )}
                        onChange={() =>
                          handleSelectProtocol(protocol.protocolId)
                        }
                      />
                    )}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200">
                    <Link
                      to={`/protocol/${protocol.protocolId}?fn=${facultyNumber}&studentName=${studentName}`}
                      className="text-blue-600 hover:underline"
                    >
                      {protocol.protocolNumber}
                    </Link>
                  </td>
                  {showCurrentProtocols === "department" && (
                    <td className="px-4 py-2 border dark:border-gray-200">
                      {protocol.teacherName}
                    </td>
                  )}
                  <td className="px-4 py-2 border dark:border-gray-200">
                    {protocol.subjectName}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200">
                    {protocol.specialityName}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200">
                    {protocol.examTypeName}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200">
                    {protocol.sessionTypeName}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200">
                    {protocol.examDate &&
                      new Date(protocol.examDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200">
                    {protocol.academicYearName}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200">
                    {protocol.semester}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200 text-center">
                    {protocol.isSigned ? (
                      <BiCheckCircle className="text-green-600 text-xl" />
                    ) : (
                      <BiXCircle className="text-red-600 text-xl" />
                    )}
                  </td>
                  <td className="px-4 py-2 border dark:border-gray-200 text-center">
                    <Link
                      to={`/protocol/${protocol.protocolId}?fn=${facultyNumber}&studentName=${studentName}`}
                      className="inline-flex items-center px-4 py-2 rounded bg-blue-600 text-white"
                    >
                      <BiLink className="w-5 h-5" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-start gap-4 items-center mt-4">
          <button
            onClick={handleSignSelectedProtocols}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <BiSolidPen className="w-5 h-5" />
            {t("SignSelectedProtocols")}
          </button>
          <button
            onClick={handleCheckProtocolsSignature}
            className={`px-4 py-2 bg-indigo-500 text-white rounded hover:bg-indigo-700 inline-flex items-center gap-2 ${
              checkingSignatures ? "opacity-50 cursor-not-allowed" : ""
            }`}
            disabled={checkingSignatures}
            title={
              checkingSignatures
                ? t("Checking signatures...")
                : t("Check protocols signatures")
            }
          >
            <BiCheckCircle className="w-5 h-5" />
            {checkingSignatures ? t("Checking...") : t("Check Signatures")}
          </button>
        </div>
        <Pagination 
          currentPage={currentPage} 
          totalPages={totalPages} 
          onPageChange={paginate}
        />
      </div>
    </div>
  );
};

export default ProtocolsTable;
